import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  LayoutDashboard, Zap, Crown, DollarSign, Users, RefreshCw,
  CheckCircle, XCircle, Clock, Loader, TrendingUp, Filter, Megaphone, ArrowRight
} from 'lucide-react';

function AdminSubscriptionDashboard({ user }) {
  const [appSubscriptions, setAppSubscriptions] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [signupVisits, setSignupVisits] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      // 보조 컬렉션은 실패해도 대시보드 전체가 죽지 않도록 빈 배열 폴백
      const safeGetDocs = async (name) => {
        try {
          const snap = await getDocs(collection(db, name));
          return snap.docs.map(d => ({ id: d.id, ...d.data() }));
        } catch (e) {
          console.error(`${name} 로드 실패 (빈 데이터로 대체):`, e);
          return [];
        }
      };

      const [appSnap, memberSnap, paymentList, visitList, userList] = await Promise.all([
        getDocs(collection(db, 'appSubscriptions')),
        getDocs(collection(db, 'memberships')),
        safeGetDocs('paymentHistory'),
        safeGetDocs('signupVisits'),
        safeGetDocs('users'),
      ]);

      const appList = appSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const memberList = memberSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      setPaymentHistory(paymentList);
      setSignupVisits(visitList);
      setAllUsers(userList);

      appList.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      memberList.sort((a, b) => {
        const dateA = a.startDate?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
        const dateB = b.startDate?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
        return dateB - dateA;
      });

      setAppSubscriptions(appList);
      setMemberships(memberList);
    } catch (error) {
      console.error('대시보드 데이터 로드 실패:', error);
      alert('데이터를 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) loadData();
  }, [user, loadData]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '0원';
    return amount.toLocaleString() + '원';
  };

  const getStatusBadge = (status) => {
    const config = {
      active: { bg: '#d1fae5', color: '#065f46', icon: <CheckCircle size={12} />, text: '활성' },
      cancelled: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={12} />, text: '취소' },
      trial: { bg: '#dbeafe', color: '#1e40af', icon: <Clock size={12} />, text: '체험' },
      expired: { bg: '#f3f4f6', color: '#6b7280', icon: <Clock size={12} />, text: '만료' },
    };
    const s = config[status] || config.active;
    return (
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '3px 8px', backgroundColor: s.bg, color: s.color,
        borderRadius: '20px', fontSize: '11px', fontWeight: '600',
      }}>
        {s.icon}{s.text}
      </span>
    );
  };

  // Stats calculations
  const totalAppSubscribers = appSubscriptions.length;
  const activeAppSubscribers = appSubscriptions.filter(s => s.status === 'active').length;
  const totalMembershipMembers = memberships.length;
  const activeMembershipMembers = memberships.filter(m => m.status === 'active').length;

  const appMonthlyRevenue = appSubscriptions
    .filter(s => s.status === 'active')
    .reduce((sum, s) => sum + (s.amount || s.monthlyFee || 0), 0); // 실스키마는 amount (monthlyFee는 레거시 폴백)

  const membershipMonthlyRevenue = memberships
    .filter(m => m.status === 'active')
    .reduce((sum, m) => sum + (m.monthlyFee || 0), 0);

  const totalMonthlyRevenue = appMonthlyRevenue + membershipMonthlyRevenue;

  // ===== 섹션 A: 체험 → 유료 전환 퍼널 =====
  const now = new Date();
  const toDateSafe = (ts) => (ts?.toDate ? ts.toDate() : (ts ? new Date(ts) : null));

  // 체험 시작: trialGranted==true 문서의 userId 집합
  const trialDocs = appSubscriptions.filter(s => s.trialGranted === true);
  const trialStartIds = new Set(trialDocs.map(s => s.userId || s.id));

  // 체험 중: expiresAt 미래 & status in ['trial','active']
  const trialActiveDocs = trialDocs.filter(s => {
    const exp = toDateSafe(s.expiresAt);
    return exp && exp > now && ['trial', 'active'].includes(s.status);
  });
  const trialActiveIds = new Set(trialActiveDocs.map(s => s.userId || s.id));

  // 결제 발생: paymentHistory type=='subscription' 의 userId 집합
  const paidUserIds = new Set(
    paymentHistory.filter(p => p.type === 'subscription' && p.userId).map(p => p.userId)
  );

  // 전환 = 체험 시작 ∩ 결제 발생
  const convertedIds = new Set([...trialStartIds].filter(id => paidUserIds.has(id)));
  // 만료 미전환 = 체험 시작 − 체험 중 − 전환
  const expiredNoConvertIds = new Set(
    [...trialStartIds].filter(id => !trialActiveIds.has(id) && !convertedIds.has(id))
  );
  const conversionDenom = convertedIds.size + expiredNoConvertIds.size;
  const conversionRate = conversionDenom > 0
    ? ((convertedIds.size / conversionDenom) * 100).toFixed(1) + '%'
    : '-';

  // 체험 중 목록 (만료 임박 순, D-day 계산)
  const trialActiveList = trialActiveDocs
    .map(s => {
      const exp = toDateSafe(s.expiresAt);
      const dday = Math.ceil((exp - now) / (24 * 60 * 60 * 1000));
      return { ...s, expDate: exp, dday };
    })
    .sort((a, b) => a.expDate - b.expDate);

  // ===== 섹션 B: UTM 채널별 유입 =====
  const DAY30 = 30 * 24 * 60 * 60 * 1000;
  const channelMap = {}; // key → { visitsTotal, visits30, signups, subInfos: Set }
  const getChannel = (key) => {
    if (!channelMap[key]) channelMap[key] = { visitsTotal: 0, visits30: 0, signups: 0, subInfos: new Set() };
    return channelMap[key];
  };

  signupVisits.forEach(v => {
    const src = v.utm?.utm_source || 'direct';
    const ch = getChannel(src);
    ch.visitsTotal += 1;
    const vAt = toDateSafe(v.visitedAt);
    if (vAt && (now - vAt) <= DAY30) ch.visits30 += 1;
    const medium = v.utm?.utm_medium;
    const campaign = v.utm?.utm_campaign;
    if (medium || campaign) ch.subInfos.add([medium, campaign].filter(Boolean).join(' / '));
  });

  allUsers.forEach(u => {
    const src = u.utm?.utm_source || 'direct'; // utm 없는 가입자는 direct/미상으로 집계
    const ch = getChannel(src);
    ch.signups += 1;
    const medium = u.utm?.utm_medium;
    const campaign = u.utm?.utm_campaign;
    if (medium || campaign) ch.subInfos.add([medium, campaign].filter(Boolean).join(' / '));
  });

  const channelRows = Object.entries(channelMap)
    .map(([source, d]) => ({
      source,
      ...d,
      subInfos: [...d.subInfos],
      convRate: d.visitsTotal > 0 ? ((d.signups / d.visitsTotal) * 100).toFixed(1) + '%' : '-',
    }))
    .sort((a, b) => b.signups - a.signups || b.visitsTotal - a.visitsTotal);

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>대시보드 데이터를 불러오는 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <LayoutDashboard size={28} color="#6366f1" />
            구독 대시보드
          </h1>
          <p style={styles.subtitle}>앱 구독 및 멤버십 현황을 한눈에 확인하세요</p>
        </div>
        <button style={styles.refreshBtn} onClick={loadData}>
          <RefreshCw size={18} />
          새로고침
        </button>
      </div>

      {/* Dashboard Cards */}
      <div style={styles.cardsGrid}>
        <div style={styles.dashCard}>
          <div style={styles.dashCardHeader}>
            <div style={{ ...styles.dashCardIcon, backgroundColor: '#e0e7ff' }}>
              <Zap size={24} color="#6366f1" />
            </div>
            <TrendingUp size={20} color="#10b981" />
          </div>
          <div style={styles.dashCardValue}>{totalAppSubscribers}</div>
          <div style={styles.dashCardLabel}>앱 구독자 (전체)</div>
          <div style={styles.dashCardSub}>{activeAppSubscribers}명 활성</div>
        </div>

        <div style={styles.dashCard}>
          <div style={styles.dashCardHeader}>
            <div style={{ ...styles.dashCardIcon, backgroundColor: '#fef3c7' }}>
              <Crown size={24} color="#f59e0b" />
            </div>
            <TrendingUp size={20} color="#10b981" />
          </div>
          <div style={styles.dashCardValue}>{totalMembershipMembers}</div>
          <div style={styles.dashCardLabel}>멤버십 회원 (전체)</div>
          <div style={styles.dashCardSub}>{activeMembershipMembers}명 활성</div>
        </div>

        <div style={styles.dashCard}>
          <div style={styles.dashCardHeader}>
            <div style={{ ...styles.dashCardIcon, backgroundColor: '#d1fae5' }}>
              <DollarSign size={24} color="#10b981" />
            </div>
          </div>
          <div style={styles.dashCardValue}>{formatCurrency(totalMonthlyRevenue)}</div>
          <div style={styles.dashCardLabel}>월 총 수익</div>
          <div style={styles.dashCardSub}>
            앱: {formatCurrency(appMonthlyRevenue)} / 멤버십: {formatCurrency(membershipMonthlyRevenue)}
          </div>
        </div>

        <div style={styles.dashCard}>
          <div style={styles.dashCardHeader}>
            <div style={{ ...styles.dashCardIcon, backgroundColor: '#ede9fe' }}>
              <Users size={24} color="#8b5cf6" />
            </div>
          </div>
          <div style={styles.dashCardValue}>{activeAppSubscribers + activeMembershipMembers}</div>
          <div style={styles.dashCardLabel}>전체 활성 구독</div>
          <div style={styles.dashCardSub}>
            앱 {activeAppSubscribers}명 + 멤버십 {activeMembershipMembers}명
          </div>
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div style={styles.revenueSection}>
        <h2 style={styles.sectionTitle}>
          <DollarSign size={22} color="#6366f1" />
          월 수익 내역
        </h2>
        <div style={styles.revenueGrid}>
          <div style={styles.revenueCard}>
            <div style={styles.revenueCardHeader}>
              <Zap size={18} color="#6366f1" />
              <span>앱 구독 수익</span>
            </div>
            <div style={styles.revenueCardValue}>{formatCurrency(appMonthlyRevenue)}</div>
            <div style={styles.revenueCardCount}>{activeAppSubscribers}건 활성 구독</div>
          </div>
          <div style={styles.revenueCard}>
            <div style={styles.revenueCardHeader}>
              <Crown size={18} color="#f59e0b" />
              <span>멤버십 수익</span>
            </div>
            <div style={styles.revenueCardValue}>{formatCurrency(membershipMonthlyRevenue)}</div>
            <div style={styles.revenueCardCount}>{activeMembershipMembers}건 활성 멤버십</div>
          </div>
        </div>
      </div>

      {/* 체험 → 유료 전환 퍼널 */}
      <div style={styles.revenueSection}>
        <h2 style={styles.sectionTitle}>
          <Filter size={22} color="#6366f1" />
          {'체험 → 유료 전환 퍼널'}
        </h2>
        <div style={styles.funnelGrid}>
          <div style={styles.funnelCard}>
            <div style={styles.funnelLabel}>체험 시작</div>
            <div style={styles.funnelValue}>{trialStartIds.size}</div>
            <div style={styles.funnelSub}>trialGranted 계정</div>
          </div>
          <div style={styles.funnelArrow}><ArrowRight size={18} color="#94a3b8" /></div>
          <div style={styles.funnelCard}>
            <div style={styles.funnelLabel}>체험 중</div>
            <div style={{ ...styles.funnelValue, color: '#1e40af' }}>{trialActiveIds.size}</div>
            <div style={styles.funnelSub}>만료 전 계정</div>
          </div>
          <div style={styles.funnelArrow}><ArrowRight size={18} color="#94a3b8" /></div>
          <div style={styles.funnelCard}>
            <div style={styles.funnelLabel}>만료 미전환</div>
            <div style={{ ...styles.funnelValue, color: '#b91c1c' }}>{expiredNoConvertIds.size}</div>
            <div style={styles.funnelSub}>결제 없이 만료</div>
          </div>
          <div style={styles.funnelArrow}><ArrowRight size={18} color="#94a3b8" /></div>
          <div style={{ ...styles.funnelCard, borderColor: '#6366f1', backgroundColor: '#eef2ff' }}>
            <div style={styles.funnelLabel}>유료 전환</div>
            <div style={{ ...styles.funnelValue, color: '#6366f1' }}>{convertedIds.size}</div>
            <div style={styles.funnelSub}>전환율 {conversionRate}</div>
          </div>
        </div>

        <div style={{ ...styles.sectionCard, marginTop: '16px' }}>
          <div style={styles.sectionCardHeader}>
            <h2 style={styles.sectionCardTitle}>
              <Clock size={20} color="#1e40af" />
              체험 중 계정 (만료 임박 순)
            </h2>
            <span style={styles.countBadge}>{trialActiveList.length}</span>
          </div>
          {trialActiveList.length === 0 ? (
            <div style={styles.sectionEmpty}>
              <Clock size={40} color="#cbd5e1" />
              <p>체험 중인 계정이 없습니다</p>
            </div>
          ) : (
            <div style={styles.listContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>User ID</th>
                    <th style={styles.th}>상태</th>
                    <th style={styles.th}>만료일</th>
                    <th style={styles.th}>D-day</th>
                  </tr>
                </thead>
                <tbody>
                  {trialActiveList.map(t => (
                    <tr key={t.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.userId}>{t.userId || t.id}</span>
                      </td>
                      <td style={styles.td}>{getStatusBadge(t.status)}</td>
                      <td style={styles.td}>
                        <span style={styles.dateText}>{formatDate(t.expiresAt)}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={t.dday <= 7 ? styles.ddayUrgent : styles.ddayNormal}>
                          D-{t.dday}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* UTM 채널별 유입 */}
      <div style={styles.revenueSection}>
        <h2 style={styles.sectionTitle}>
          <Megaphone size={22} color="#f59e0b" />
          UTM 채널별 유입
        </h2>
        <p style={styles.utmCaption}>
          {'utm_source=reactivation이 파일럿 발송 채널입니다. 발송 후 이 표에 유입이 집계됩니다.'}
        </p>
        <div style={styles.sectionCard}>
          <div style={styles.listContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>채널</th>
                  <th style={styles.th}>가입 방문 (30일 / 전체)</th>
                  <th style={styles.th}>가입 수</th>
                  <th style={styles.th}>{'방문 → 가입 %'}</th>
                </tr>
              </thead>
              <tbody>
                {channelRows.length === 0 ? (
                  <tr>
                    <td style={{ ...styles.td, color: '#94a3b8' }} colSpan={4}>
                      아직 집계된 유입 데이터가 없습니다
                    </td>
                  </tr>
                ) : (
                  channelRows.map(row => (
                    <tr key={row.source} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.channelName}>
                          {row.source === 'direct' ? 'direct/미상' : row.source}
                        </span>
                        {row.subInfos.length > 0 && (
                          <div style={styles.channelSub}>{row.subInfos.join(', ')}</div>
                        )}
                      </td>
                      <td style={styles.td}>
                        <strong>{row.visits30}</strong>
                        <span style={styles.dateText}> / {row.visitsTotal}</span>
                      </td>
                      <td style={styles.td}>
                        <strong style={styles.amount}>{row.signups}</strong>
                      </td>
                      <td style={styles.td}>{row.convRate}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Two column sections */}
      <div style={styles.twoColumnLayout}>
        {/* App Subscriptions Section */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionCardHeader}>
            <h2 style={styles.sectionCardTitle}>
              <Zap size={20} color="#6366f1" />
              앱 구독 목록
            </h2>
            <span style={styles.countBadge}>{appSubscriptions.length}</span>
          </div>
          {appSubscriptions.length === 0 ? (
            <div style={styles.sectionEmpty}>
              <Zap size={40} color="#cbd5e1" />
              <p>앱 구독자가 없습니다</p>
            </div>
          ) : (
            <div style={styles.listContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>User ID</th>
                    <th style={styles.th}>월 요금</th>
                    <th style={styles.th}>상태</th>
                    <th style={styles.th}>시작일</th>
                  </tr>
                </thead>
                <tbody>
                  {appSubscriptions.map(sub => (
                    <tr key={sub.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.userId}>{sub.userId || sub.id}</span>
                      </td>
                      <td style={styles.td}>
                        <strong style={styles.amount}>{formatCurrency(sub.amount || sub.monthlyFee || sub.totalAmount)}</strong>
                      </td>
                      <td style={styles.td}>
                        {getStatusBadge(sub.status || 'active')}
                      </td>
                      <td style={styles.td}>
                        <span style={styles.dateText}>{formatDate(sub.startDate || sub.createdAt)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Membership Section */}
        <div style={styles.sectionCard}>
          <div style={styles.sectionCardHeader}>
            <h2 style={styles.sectionCardTitle}>
              <Crown size={20} color="#f59e0b" />
              멤버십 목록
            </h2>
            <span style={styles.countBadge}>{memberships.length}</span>
          </div>
          {memberships.length === 0 ? (
            <div style={styles.sectionEmpty}>
              <Crown size={40} color="#cbd5e1" />
              <p>멤버십 회원이 없습니다</p>
            </div>
          ) : (
            <div style={styles.listContainer}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>User ID</th>
                    <th style={styles.th}>할인율</th>
                    <th style={styles.th}>월 요금</th>
                    <th style={styles.th}>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {memberships.map(m => (
                    <tr key={m.id} style={styles.tr}>
                      <td style={styles.td}>
                        <span style={styles.userId}>{m.userId || m.id}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.tierBadge}>{m.tierPercent || 0}%</span>
                      </td>
                      <td style={styles.td}>
                        <strong style={styles.amount}>{formatCurrency(m.monthlyFee)}</strong>
                      </td>
                      <td style={styles.td}>
                        {getStatusBadge(m.status || 'active')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        table tr:hover { background-color: #f8fafc; }
      `}</style>
    </div>
  );
}

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '24px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' },
  title: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: '700', color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '14px', color: '#64748b', margin: '8px 0 0 0' },
  refreshBtn: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px', backgroundColor: '#f1f5f9', color: '#475569', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },

  // Loading
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', color: '#64748b' },
  spinner: { width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },

  // Dashboard cards
  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '16px', marginBottom: '32px' },
  dashCard: { padding: '24px', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', transition: 'box-shadow 0.2s' },
  dashCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  dashCardIcon: { width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' },
  dashCardValue: { fontSize: '28px', fontWeight: '800', color: '#0f172a', marginBottom: '4px' },
  dashCardLabel: { fontSize: '14px', fontWeight: '600', color: '#64748b' },
  dashCardSub: { fontSize: '12px', color: '#94a3b8', marginTop: '4px' },

  // Revenue section
  revenueSection: { marginBottom: '32px' },
  sectionTitle: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '20px', fontWeight: '700', color: '#0f172a', margin: '0 0 16px 0' },
  revenueGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' },
  revenueCard: { padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  revenueCardHeader: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', fontWeight: '600', color: '#475569', marginBottom: '12px' },
  revenueCardValue: { fontSize: '24px', fontWeight: '800', color: '#6366f1', marginBottom: '4px' },
  revenueCardCount: { fontSize: '13px', color: '#94a3b8' },

  // Two column
  twoColumnLayout: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))', gap: '24px' },
  sectionCard: { backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' },
  sectionCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #e2e8f0' },
  sectionCardTitle: { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 },
  countBadge: { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: '28px', height: '28px', padding: '0 10px', backgroundColor: '#e0e7ff', color: '#4338ca', borderRadius: '14px', fontSize: '13px', fontWeight: '700' },
  sectionEmpty: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 20px', color: '#94a3b8', gap: '8px', fontSize: '14px' },

  listContainer: { maxHeight: '400px', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc', position: 'sticky', top: 0 },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' },
  td: { padding: '12px 16px', fontSize: '13px', color: '#0f172a' },

  // 전환 퍼널
  funnelGrid: { display: 'flex', alignItems: 'stretch', gap: '8px', flexWrap: 'wrap' },
  funnelCard: { flex: '1 1 160px', minWidth: '150px', padding: '18px 20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  funnelLabel: { fontSize: '13px', fontWeight: '600', color: '#64748b', marginBottom: '6px' },
  funnelValue: { fontSize: '26px', fontWeight: '800', color: '#0f172a' },
  funnelSub: { fontSize: '12px', color: '#94a3b8', marginTop: '4px' },
  funnelArrow: { display: 'flex', alignItems: 'center', flex: '0 0 auto' },
  ddayUrgent: { display: 'inline-block', padding: '2px 8px', backgroundColor: '#fee2e2', color: '#b91c1c', borderRadius: '12px', fontSize: '12px', fontWeight: '700' },
  ddayNormal: { display: 'inline-block', padding: '2px 8px', backgroundColor: '#f1f5f9', color: '#475569', borderRadius: '12px', fontSize: '12px', fontWeight: '600' },

  // UTM 채널
  utmCaption: { fontSize: '12px', color: '#94a3b8', margin: '-8px 0 12px 0' },
  channelName: { fontSize: '13px', fontWeight: '700', color: '#0f172a' },
  channelSub: { fontSize: '11px', color: '#94a3b8', marginTop: '2px' },

  userId: { fontFamily: 'monospace', fontSize: '12px', color: '#6366f1', fontWeight: '600' },
  amount: { color: '#6366f1', fontWeight: '700', fontSize: '13px' },
  tierBadge: { display: 'inline-block', padding: '2px 8px', backgroundColor: '#e0e7ff', color: '#4338ca', borderRadius: '12px', fontSize: '12px', fontWeight: '700' },
  dateText: { fontSize: '12px', color: '#64748b' },
};

export default AdminSubscriptionDashboard;
